import { useEffect, useState } from 'react';

import { Form, Input, message, Modal, Spin, Upload } from 'antd';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import type { UploadChangeParam, UploadFile, UploadProps } from 'antd/es/upload';

import { uploadPostCollectionImageUrl } from '@/Axios/api/Media';
import { customAxios } from '@/Axios/index';
import { useAppSelector } from '@/Hooks/useAppRedux';

export interface MyPageProfileDetail {
  profileID?: string;
  name?: string;
  description?: string;
  canChangeNameByRole?: boolean;
  profileIconId?: string | null;
  profileIconUrl?: string | null;
}

/**
 * Ant Design sets `file.response` to the upload HTTP body. This endpoint may return
 * one object or an array of one row (same pattern as other CMS image uploads), and
 * either profile fields or legacy imageId/imageUrl.
 */
function parseProfileIconFromResponse(file: UploadFile): { id: string; url: string } | null {
  const raw = file.response as unknown;
  if (raw == null) return null;
  const row = Array.isArray(raw) ? raw[0] : raw;
  if (row == null || typeof row !== 'object') return null;
  const o = row as Record<string, unknown>;
  const id = o.profileIconId ?? o.imageId;
  const url = o.profileIconUrl ?? o.imageUrl;
  if (id != null && url != null) return { id: String(id), url: String(url) };
  return null;
}

function doneProfileIconUploadFile(id: string | undefined, url: string): UploadFile {
  return { uid: id || 'profile-icon', name: 'profile-icon', status: 'done', url };
}

const uploadMessageStyle = { marginTop: '120px' as const };

const MyPageProfileDetailModal = ({
  open,
  onConfirm,
  setModalOpen,
}: {
  open: boolean;
  onConfirm: (values: MyPageProfileDetail) => void;
  setModalOpen: (value: boolean) => void;
}) => {
  const { loadingMyPageProfileData } = useAppSelector((state) => state.userRole);
  const { myPageProfileData } = useAppSelector((state) => state.userRole.value);

  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [profileIconId, setProfileIconId] = useState<string | undefined>();
  const [profileIconUrl, setProfileIconUrl] = useState<string | undefined>();

  const bearerToken = customAxios.defaults.headers.common.Authorization;
  const uploadActionUrl = `${customAxios.defaults.baseURL}${uploadPostCollectionImageUrl}`;
  const canChangeNameByRole = myPageProfileData?.canChangeNameByRole ?? false;

  useEffect(() => {
    if (open && myPageProfileData && !loadingMyPageProfileData) {
      form.setFieldsValue({
        profileID: myPageProfileData.profileID || '',
        name: myPageProfileData.name || '',
        description: myPageProfileData.description || '',
        canChangeNameByRole: myPageProfileData.canChangeNameByRole || false,
      });
      const iconId = myPageProfileData.profileIconId;
      const iconUrl = myPageProfileData.profileIconUrl;
      setProfileIconId(iconId);
      setProfileIconUrl(iconUrl);
      if (iconUrl) {
        setFileList([doneProfileIconUploadFile(iconId, iconUrl)]);
      } else {
        setFileList([]);
      }
      return;
    }
    if (!open) {
      setFileList([]);
      setProfileIconId(undefined);
      setProfileIconUrl(undefined);
    }
  }, [form, open, myPageProfileData, loadingMyPageProfileData]);

  const clearProfileIcon = () => {
    setUploadLoading(false);
    setFileList([]);
    setProfileIconId(undefined);
    setProfileIconUrl(undefined);
  };

  const showProfileIconFromIdAndUrl = (id: string | undefined, url: string) => {
    setProfileIconId(id);
    setProfileIconUrl(url);
    setFileList([doneProfileIconUploadFile(id, url)]);
  };

  const handleUploadChange: UploadProps['onChange'] = (param: UploadChangeParam<UploadFile>) => {
    const { file } = param;
    switch (file.status) {
      case 'uploading':
        setUploadLoading(true);
        setFileList([file]);
        break;
      case 'done': {
        setUploadLoading(false);
        setFileList([file]);
        const parsed = parseProfileIconFromResponse(file);
        if (parsed) {
          setProfileIconId(parsed.id);
          setProfileIconUrl(parsed.url);
        } else {
          message.error({
            content: 'Unexpected upload response; could not read profile icon.',
            duration: 3,
            style: uploadMessageStyle,
          });
          clearProfileIcon();
        }
        break;
      }
      case 'removed':
        clearProfileIcon();
        break;
      case 'error':
        setUploadLoading(false);
        message.error({
          content: 'Upload failed; please try again.',
          duration: 3,
          style: uploadMessageStyle,
        });
        if (myPageProfileData?.profileIconUrl) {
          showProfileIconFromIdAndUrl(
            myPageProfileData.profileIconId,
            myPageProfileData.profileIconUrl,
          );
        } else {
          clearProfileIcon();
        }
        break;
      default:
        break;
    }
  };

  const onConfirmClick = async () => {
    if (uploadLoading) return;
    const values = form.getFieldsValue(true) as Omit<MyPageProfileDetail, 'profileIconId' | 'profileIconUrl'>;
    onConfirm({
      ...values,
      profileIconId: profileIconId ?? null,
      profileIconUrl: profileIconUrl ?? null,
    });
    setModalOpen(false);
  };

  const uploadButton = (
    <div>
      {uploadLoading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  return (
    <Modal
      open={open}
      destroyOnClose
      onOk={onConfirmClick}
      onCancel={() => setModalOpen(false)}
      title="My Page Profile Detail"
      okButtonProps={{ disabled: uploadLoading }}
      cancelButtonProps={{ disabled: uploadLoading }}
    >
      <Spin spinning={loadingMyPageProfileData}>
        <Form form={form} preserve={false} layout="vertical">
          <Form.Item label="Profile icon">
            <Upload
              listType="picture-card"
              action={uploadActionUrl}
              headers={
                typeof bearerToken === 'string'
                  ? { authorization: bearerToken }
                  : { authorization: String(bearerToken ?? '') }
              }
              name="images"
              accept="image/*"
              maxCount={1}
              fileList={fileList}
              onChange={handleUploadChange}
              disabled={uploadLoading}
            >
              {fileList.length >= 1 ? null : uploadButton}
            </Upload>
          </Form.Item>
          <Form.Item name="profileID" label="Profile ID">
            <Input />
          </Form.Item>
          <Form.Item name="name" label="Name">
            <Input disabled={canChangeNameByRole} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={5} />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default MyPageProfileDetailModal;
